import { rewriteCatalogCdnUrl, rewriteCatalogCdnUrls } from './config/cdn';
import { shareAsync } from './share-async';

export type PortalCalendarEvent = {
  id: string;
  gameKey: string | null;
  gameName: string;
  coverUrl: string;
  eventDate: string;
  eventType: string;
  title: string;
  detail: string;
  highlighted: boolean;
  month: string;
  day: string;
};

export type PortalNewsItem = {
  id: string;
  gameKey: string | null;
  gameName: string;
  coverUrl: string;
  title: string;
  tag: string;
  summary: string;
  body: string;
  imgList: string[];
  videoList: string[];
  publishedAt: string;
  date: string;
};

export type PortalFeedPage<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
};

/** 日历日期线高亮：仅当天（上海时区）；列表无今日则不高亮 */
export function resolveActiveCalendarDate(
  events: Array<{ eventDate: string }>,
  now = new Date(),
): string {
  if (!events.length) return '';
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return events.some((event) => event.eventDate === today) ? today : '';
}

function emptyPage<T>(pageSize: number): PortalFeedPage<T> {
  return { items: [], page: 1, pageSize, total: 0, hasMore: false };
}

function rewriteCalendarItem(item: PortalCalendarEvent): PortalCalendarEvent {
  return { ...item, coverUrl: rewriteCatalogCdnUrl(item.coverUrl) };
}

function rewriteNewsItem(item: PortalNewsItem): PortalNewsItem {
  return {
    ...item,
    coverUrl: rewriteCatalogCdnUrl(item.coverUrl),
    imgList: rewriteCatalogCdnUrls(item.imgList),
    videoList: rewriteCatalogCdnUrls(item.videoList),
  };
}

async function fetchFeedPage<T>(
  path: string,
  params: Record<string, string | number | undefined>,
): Promise<PortalFeedPage<T>> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  const response = await fetch(`${path}?${query}`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`feed request failed: ${response.status}`);
  return (await response.json()) as PortalFeedPage<T>;
}

function feedCacheKey(
  kind: string,
  params: Record<string, string | number | undefined>,
) {
  return `${kind}:${Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=${value}`)
    .join('&')}`;
}

const calendarInflight = new Map<
  string,
  Promise<PortalFeedPage<PortalCalendarEvent>>
>();
const newsInflight = new Map<string, Promise<PortalFeedPage<PortalNewsItem>>>();

export async function fetchPortalCalendar(params: {
  page?: number;
  pageSize?: number;
  year?: number;
  month?: number;
  game?: string;
} = {}) {
  const request = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    year: params.year,
    month: params.month,
    game: params.game,
  };
  return shareAsync(
    calendarInflight,
    feedCacheKey('calendar', request),
    async () => {
      try {
        const page = await fetchFeedPage<PortalCalendarEvent>(
          '/api/portal/calendar',
          request,
        );
        return { ...page, items: page.items.map(rewriteCalendarItem) };
      } catch {
        return emptyPage<PortalCalendarEvent>(request.pageSize);
      }
    },
    4000,
  );
}

export async function fetchPortalNews(params: {
  page?: number;
  pageSize?: number;
  tag?: string;
  game?: string;
} = {}) {
  const request = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    tag: params.tag,
    game: params.game,
  };
  return shareAsync(
    newsInflight,
    feedCacheKey('news', request),
    async () => {
      try {
        const page = await fetchFeedPage<PortalNewsItem>(
          '/api/portal/news',
          request,
        );
        return { ...page, items: page.items.map(rewriteNewsItem) };
      } catch {
        return emptyPage<PortalNewsItem>(request.pageSize);
      }
    },
    4000,
  );
}
